import {
  Box,
  Button,
  ListSubheader,
  Menu,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import React, { useState } from "react";

import { monospaceStyle } from "../../theme";
import { EntityRef } from "../../types/productPlane";
import { ChainDirection, ClassFold } from "../../types/valueChain";
import {
  ACTIVE_LENSES,
  ACTIVE_VIEWS,
  ChainLens,
  ChainView,
  MAX_CHAIN_DEPTH,
  MIN_CHAIN_DEPTH,
} from "./valueChainState";

// Each direction with its words and the name it is announced by
const DIRECTIONS: { value: ChainDirection; label: string; name: string }[] = [
  { value: "hosts", label: "← Built into", name: "Built into" },
  { value: "both", label: "Both", name: "Both" },
  { value: "parts", label: "Parts →", name: "Parts" },
];

const LENSES: { value: ChainLens; label: string }[] = [
  { value: "makers", label: "Makers" },
  { value: "jurisdiction", label: "Jurisdiction" },
  { value: "facilities", label: "Facilities" },
];

// The depths a request may read, nearest first
const DEPTHS = Array.from(
  { length: MAX_CHAIN_DEPTH - MIN_CHAIN_DEPTH + 1 },
  (_unused, index) => MIN_CHAIN_DEPTH + index,
);

// A segment of the toolbar: as tall as the root field
export const SEGMENT_SX = {
  textTransform: "none",
  height: 40,
  px: 1.75,
  fontSize: 13,
  fontWeight: 500,
} as const;

// A button of the toolbar that reads as a chip: "Depth 2 ▾", "Fit"
export const TOOL_BUTTON_SX = {
  textTransform: "none",
  height: 32,
  px: 1.25,
  minWidth: 0,
  fontSize: "12.5px",
  fontWeight: 400,
  color: "text.primary",
  borderColor: "rgba(255, 255, 255, 0.1)",
  backgroundColor: "rgba(255, 255, 255, 0.03)",
  "&:hover": { borderColor: "rgba(255, 255, 255, 0.2)" },
} as const;

interface ValueChainControlsProps {
  direction: ChainDirection;
  onDirectionChange: (direction: ChainDirection) => void;
  // The directions offered; every one when left out
  directions?: readonly ChainDirection[];
  // Steps read towards the parts
  partsDepth: number;
  onPartsDepthChange: (depth: number) => void;
  // Steps read towards the hosts
  hostsDepth: number;
  onHostsDepthChange: (depth: number) => void;
  lens: ChainLens;
  onLensChange: (lens: ChainLens) => void;
  // Whether product mentions that carry no name are read
  unnamed: boolean;
  onUnnamedChange: (unnamed: boolean) => void;
  // Which classes a product stands on
  classes: ClassFold;
  onClassesChange: (classes: ClassFold) => void;
  // Whether the typical-host rows are read as edges
  typical: boolean;
  onTypicalChange: (typical: boolean) => void;
}

// One side's depth, as the menu offers it
interface DepthSide {
  key: "hosts" | "parts";
  title: string;
  depth: number;
  onChange: (depth: number) => void;
}

// The depth as one menu: "Depth 2 ▾", with a list of depths for each side
// the direction reads, each under its own head when both sides are read
const DepthMenu: React.FC<{
  sides: DepthSide[];
}> = ({ sides }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const both = sides.length > 1;
  const depths = sides.map((side) => side.depth);
  const shown = depths.every((depth) => depth === depths[0])
    ? String(depths[0])
    : depths.join(" · ");
  const items: React.ReactNode[] = [];
  for (const side of sides) {
    if (both) {
      items.push(
        <ListSubheader
          key={`${side.key}-head`}
          sx={{ lineHeight: "32px", backgroundColor: "transparent" }}
        >
          {side.title}
        </ListSubheader>,
      );
    }
    for (const depth of DEPTHS) {
      items.push(
        <MenuItem
          key={`${side.key}-${depth}`}
          dense
          selected={depth === side.depth}
          aria-label={both ? `${side.title} depth ${depth}` : `Depth ${depth}`}
          onClick={() => {
            setAnchor(null);
            if (depth !== side.depth) {
              side.onChange(depth);
            }
          }}
        >
          <Box component="span" sx={monospaceStyle}>
            {depth}
          </Box>
          &nbsp;{depth === 1 ? "step" : "steps"}
        </MenuItem>,
      );
    }
  }
  return (
    <>
      <Button
        variant="outlined"
        size="small"
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        aria-label={`Depth ${shown}`}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={TOOL_BUTTON_SX}
      >
        Depth&nbsp;
        <Box component="span" sx={monospaceStyle}>
          {shown}
        </Box>
        <Box component="span" sx={{ color: "text.disabled", ml: 0.75 }}>
          ▾
        </Box>
      </Button>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
      >
        {items}
      </Menu>
    </>
  );
};

// One switch of the read menu: ticked when it is on
const ReadItem: React.FC<{
  label: string;
  checked: boolean;
  onToggle: () => void;
}> = ({ label, checked, onToggle }) => (
  <MenuItem
    dense
    role="menuitemcheckbox"
    aria-checked={checked}
    onClick={onToggle}
  >
    <Box
      component="span"
      aria-hidden
      sx={{
        width: 18,
        color: "link.main",
        visibility: checked ? "visible" : "hidden",
      }}
    >
      ✓
    </Box>
    {label}
  </MenuItem>
);

// What the chain reads beyond the stated pairs of named products, as one
// menu: "Read ▾", with a tick for each switch that is on. It stays open
// after a click, so that several can be changed in one go.
const ReadMenu: React.FC<{
  unnamed: boolean;
  onUnnamedChange: (unnamed: boolean) => void;
  classes: ClassFold;
  onClassesChange: (classes: ClassFold) => void;
  typical: boolean;
  onTypicalChange: (typical: boolean) => void;
}> = ({
  unnamed,
  onUnnamedChange,
  classes,
  onClassesChange,
  typical,
  onTypicalChange,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Button
        variant="outlined"
        size="small"
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        aria-label="Read"
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={TOOL_BUTTON_SX}
      >
        Read
        <Box component="span" sx={{ color: "text.disabled", ml: 0.75 }}>
          ▾
        </Box>
      </Button>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
      >
        <ReadItem
          label="Unnamed mentions"
          checked={unnamed}
          onToggle={() => onUnnamedChange(!unnamed)}
        />
        <ReadItem
          label="Every class of a product"
          checked={classes === "every"}
          onToggle={() =>
            onClassesChange(classes === "every" ? "primary" : "every")
          }
        />
        <ReadItem
          label="Typical hosts"
          checked={typical}
          onToggle={() => onTypicalChange(!typical)}
        />
      </Menu>
    </>
  );
};

// The controls of the toolbar after the root field: which way the chain is
// read, what the cards say, how many steps to each side it reads, and what
// it reads beyond the stated pairs
const ValueChainControls: React.FC<ValueChainControlsProps> = ({
  direction,
  onDirectionChange,
  directions,
  partsDepth,
  onPartsDepthChange,
  hostsDepth,
  onHostsDepthChange,
  lens,
  onLensChange,
  unnamed,
  onUnnamedChange,
  classes,
  onClassesChange,
  typical,
  onTypicalChange,
}) => {
  const hosts: DepthSide = {
    key: "hosts",
    title: "Built into",
    depth: hostsDepth,
    onChange: onHostsDepthChange,
  };
  const parts: DepthSide = {
    key: "parts",
    title: "Parts",
    depth: partsDepth,
    onChange: onPartsDepthChange,
  };
  const sides =
    direction === "both" ? [hosts, parts] : direction === "hosts" ? [hosts] : [parts];

  return (
    <>
      <ToggleButtonGroup
        exclusive
        size="small"
        aria-label="Direction"
        value={direction}
        onChange={(_event, value: ChainDirection | null) => {
          if (value) {
            onDirectionChange(value);
          }
        }}
      >
        {DIRECTIONS.filter(
          (option) => !directions || directions.includes(option.value),
        ).map((option) => (
          <ToggleButton
            key={option.value}
            value={option.value}
            aria-label={option.name}
            sx={SEGMENT_SX}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <ToggleButtonGroup
        exclusive
        size="small"
        aria-label="Lens"
        value={lens}
        onChange={(_event, value: ChainLens | null) => {
          if (value) {
            onLensChange(value);
          }
        }}
      >
        {LENSES.map((option) =>
          ACTIVE_LENSES.includes(option.value) ? (
            <ToggleButton key={option.value} value={option.value} sx={SEGMENT_SX}>
              {option.label}
            </ToggleButton>
          ) : (
            <Tooltip key={option.value} title="Coming soon">
              {/* A disabled button takes no pointer: the tooltip sits on a
                  wrapper */}
              <span>
                <ToggleButton
                  value={option.value}
                  disabled
                  sx={{ ...SEGMENT_SX, borderRadius: 0 }}
                >
                  {option.label}
                </ToggleButton>
              </span>
            </Tooltip>
          ),
        )}
      </ToggleButtonGroup>

      <DepthMenu sides={sides} />
      <ReadMenu
        unnamed={unnamed}
        onUnnamedChange={onUnnamedChange}
        classes={classes}
        onClassesChange={onClassesChange}
        typical={typical}
        onTypicalChange={onTypicalChange}
      />
    </>
  );
};

// The focus view's maker highlight: the makers proven on a host, one of
// which lights its own paths
export const MakerHighlight: React.FC<{
  makers: EntityRef[];
  maker: string | null;
  onMakerChange: (maker: string | null) => void;
}> = ({ makers, maker, onMakerChange }) => (
  <TextField
    select
    size="small"
    value={maker ?? ""}
    onChange={(event) => onMakerChange(event.target.value || null)}
    SelectProps={{ native: true }}
    inputProps={{ "aria-label": "Highlight a maker" }}
    sx={{
      minWidth: 220,
      "& .MuiNativeSelect-select": { py: "6px", fontSize: "12.5px" },
    }}
  >
    <option value="">Highlight a maker's paths</option>
    {makers.map((option) => (
      <option key={option.id} value={option.id}>
        {option.name ?? option.id}
      </option>
    ))}
  </TextField>
);

const VIEWS: { value: ChainView; label: string }[] = [
  { value: "graph", label: "Graph" },
  { value: "tree", label: "Tree" },
  { value: "grouped", label: "Grouped" },
  { value: "matrix", label: "Matrix" },
];

// How the chain is laid out: the canvas, as a tree of subsystems, or by tier
// and area. Only the layouts the page offers are listed; with one offered and
// shown there is nothing to switch, and the switch is left out. A layout
// named by address alone is left by the ones offered.
export const ViewSwitch: React.FC<{
  view: ChainView;
  onViewChange: (view: ChainView) => void;
}> = ({ view, onViewChange }) => {
  const offered = VIEWS.filter((option) => ACTIVE_VIEWS.includes(option.value));
  if (offered.length <= 1 && ACTIVE_VIEWS.includes(view)) {
    return null;
  }
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      aria-label="Layout"
      value={view}
      onChange={(_event, value: ChainView | null) => {
        if (value) {
          onViewChange(value);
        }
      }}
    >
      {offered.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          sx={{ ...SEGMENT_SX, height: 32, px: 1.25 }}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default ValueChainControls;
